import * as React from 'react'
import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  Badge,
  Box,
  CollapsableCard,
  Divider,
  LazyToggleMethod,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Button as GammaButton } from '@factorialco/deprecated-design-system'
import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon, F0Select } from '@factorialco/f0-react'
import { EntityId, NumberInput } from '@factorialco/f0-react/dist/experimental'
import {
  ChevronDown,
  ChevronRight,
  Delete,
  Organization as OrganizationIcon,
  Plus,
} from '@factorialco/f0-react/icons/app'
import {
  WorkflowsValueObjectsConditionAstTermsInputDataTypeEnum,
  WorkflowsValueObjectsConditionAstTermsLiteralTypeEnum,
  WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NodeUICondition } from 'modules/workflows/components/Node/types'
import { generateUniqueLabel, getFieldErrors } from 'modules/workflows/components/Node/utils'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { ConditionalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/conditionalFormSchema'
import {
  convertArrayFromWorkflowValue,
  convertArrayToWorkflowValue,
  convertEntityFromWorkflowValue,
  convertNumberFromWorkflow,
  convertToWorkflowValue,
} from 'modules/workflows/components/Sidepanel/utils'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import {
  createAllConditionChildEdges,
  createAllConditionChildNodes,
} from 'modules/workflows/lib/flow/adapters'
import { WorkflowsComparison, WorkflowsExpression } from 'modules/workflows/lib/types'

import { BasicInputs } from './partials/BasicInputs'
import { SidepanelFooter } from './partials/SidepanelFooter'
import { SidepanelHeader } from './partials/SidepanelHeader'
import { SidepanelWrapper } from './partials/SidepanelWrapper'

const EMPTY_RIGHT_HAND_SELECTOR_VALUE = ''
const EMPTY_ARRAY_SELECTOR_VALUE = '[]'

// The literal type is determined at runtime, based on the variable type and the comparison operator
// When operator is 'one_of' or 'not_one_of', we use integer_array type for the literal
const getLiteralType = (
  variableName: string,
  variableType?: WorkflowsValueObjectsConditionAstTermsInputDataTypeEnum,
  operator?: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum
): WorkflowsValueObjectsConditionAstTermsLiteralTypeEnum => {
  // If operator is 'one_of' or 'not_one_of', return integer_array regardless of variable type
  if (operator === 'one_of' || operator === 'not_one_of') {
    return 'integer_array'
  }

  if (!variableType) {
    throw new Error(`Unknown type for variable: ${variableName}`)
  }
  switch (variableType) {
    case 'integer':
      return 'integer'
    case 'string':
      return 'string'
    case 'boolean':
      return 'boolean'
    case 'float':
      return 'float'
  }
  return 'integer'
}

// Dynamic right-hand selector component based on variable type and operator
const RightHandSelector = ({
  variableName,
  value,
  onChange,
  variablesConfig,
  errorMessage,
  operator,
}: {
  variableName: string | undefined
  value: string | undefined
  onChange: (newValue: string) => void
  variablesConfig: VariablesConfig
  errorMessage?: string
  operator?: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum
}) => {
  if (!variableName) {
    throw new Error('Variable name is required for RightHandSelector')
  }

  const { workflowManifest } = useWorkflowContext()

  // Find the variable data from useCase (source of truth)
  const variableData = workflowManifest.workflowDataInputs.find(
    (data) => data.name === variableName
  )

  if (!variableData) {
    throw new Error(`Variable data not found for: ${variableName}`)
  }

  const selectorConfig = variablesConfig[variableName]
  if (!selectorConfig) {
    throw new Error(`Selector not found in variablesConfig mapping for variable: ${variableName}`)
  }
  if (selectorConfig.component === 'number') {
    return (
      <NumberInput
        locale={i18n.locale}
        size="md"
        hideLabel
        label="Value"
        value={convertNumberFromWorkflow(value)}
        onChange={(newValue) => onChange(convertToWorkflowValue(newValue))}
        units={selectorConfig.unit}
        error={errorMessage}
        placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
      />
    )
  }

  if (selectorConfig.valueType === 'entity') {
    // Check if operator is 'one_of' or 'not_one_of' - use multi-select if available
    if (
      (operator === 'one_of' || operator === 'not_one_of') &&
      selectorConfig.multiSelectComponent
    ) {
      const MultiEntitySelector = selectorConfig.multiSelectComponent
      const entityValues = convertArrayFromWorkflowValue(value || '[]')

      const handleMultiEntityChange = (newEntityValues: EntityId[] | null) => {
        const stringValue = convertArrayToWorkflowValue(newEntityValues)
        onChange(stringValue)
      }

      return (
        <MultiEntitySelector
          value={entityValues}
          onChange={handleMultiEntityChange}
          error={errorMessage}
        />
      )
    }

    // Single select (default)
    const EntitySelector = selectorConfig.component
    const entityValue = convertEntityFromWorkflowValue(value || '')

    const handleEntityChange = (newEntityValue: EntityId | null) => {
      const stringValue = convertToWorkflowValue(newEntityValue)
      onChange(stringValue)
    }

    return <EntitySelector value={entityValue} onChange={handleEntityChange} error={errorMessage} />
  }
}

const ConditionPathCardHeader = ({
  open,
  toggle,
  priority,
  onRemove,
  canRemove,
}: {
  open: boolean
  toggle: LazyToggleMethod
  priority: number
  onRemove: () => void
  canRemove: boolean
}) => {
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      width="full"
      cursor="pointer"
      onClick={toggle(!open)}
    >
      <Box flexDirection="row" gap="s8" alignItems="center">
        <Badge>{priority}</Badge>
        <Text size="200" weight="medium">
          {i18n.t('workflows.sidepanel.condition.paths.path')}
        </Text>
        <F0Icon icon={open ? ChevronDown : ChevronRight} size="md" />
      </Box>
      {canRemove && (
        <GammaButton
          iconName="delete"
          onClick={(e) => {
            e.stopPropagation() // Prevent card toggle
            onRemove()
          }}
        />
      )}
    </Box>
  )
}

const ConditionalPathCardContent = ({
  expression,
  setExpression,
  variablesConfig,
}: {
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
}) => {
  // Operator options - includes 'one_of' and 'not_one_of' for entity multi-select
  const operatorOptions: {
    label: string
    value: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum
  }[] = [
    { label: i18n.t('workflows.node_type.condition.operations.eq'), value: 'eq' },
    { label: i18n.t('workflows.node_type.condition.operations.neq'), value: 'neq' },
    { label: i18n.t('workflows.node_type.condition.operations.one_of'), value: 'one_of' },
    { label: i18n.t('workflows.node_type.condition.operations.not_one_of'), value: 'not_one_of' },
    { label: i18n.t('workflows.node_type.condition.operations.gt'), value: 'gt' },
    { label: i18n.t('workflows.node_type.condition.operations.gte'), value: 'gte' },
    { label: i18n.t('workflows.node_type.condition.operations.lt'), value: 'lt' },
    { label: i18n.t('workflows.node_type.condition.operations.lte'), value: 'lte' },
  ]

  // Helper to check if a variable has multi-select support
  const hasMultiSelectSupport = (variableName: string): boolean => {
    const config = variablesConfig[variableName]
    return config?.valueType === 'entity' && !!config.multiSelectComponent
  }

  const getOperatorsForVariable = (variableName?: string) => {
    if (!variableName)
      return operatorOptions.filter(
        (option) => option.value !== 'one_of' && option.value !== 'not_one_of'
      )

    const config = variablesConfig[variableName]
    const variableType = config?.valueType
    const hasMultiSelect = hasMultiSelectSupport(variableName)

    switch (variableType) {
      case 'entity':
        // Include 'one_of' and 'not_one_of' operators only if multi-select component is available
        if (hasMultiSelect) {
          return operatorOptions.filter(
            (option) =>
              option.value === 'eq' ||
              option.value === 'neq' ||
              option.value === 'one_of' ||
              option.value === 'not_one_of'
          )
        }
        return operatorOptions.filter((option) => option.value === 'eq' || option.value === 'neq')
      default:
        // For non-entity types, exclude 'one_of' and 'not_one_of' operators
        return operatorOptions.filter(
          (option) => option.value !== 'one_of' && option.value !== 'not_one_of'
        )
    }
  }

  const { workflowManifest } = useWorkflowContext()

  const { getFieldState, formState } = useFormContext<ConditionalFormSchema>()

  const inputVariables = workflowManifest.workflowDataInputs.map((input) => {
    const configLabel = variablesConfig && variablesConfig[input.name]?.label
    return {
      label: configLabel ? configLabel : input.name,
      value: input.name,
    }
  })

  const comparisons = expression.root.expressions
  const firstComparison = comparisons[0]

  const handleDeleteComparison = (comparisonIndex: number) => {
    const newExpressions = comparisons.filter((_, index) => index !== comparisonIndex)

    const newExpression = {
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    }
    setExpression(newExpression)
  }

  const handleLiteralSelection = (
    comparisonIndex: number,
    newValue: string,
    type: WorkflowsValueObjectsConditionAstTermsLiteralTypeEnum
  ) => {
    // Validate the value based on type
    // For number types, allow empty string (for in-progress editing) or valid numbers
    if (type === 'integer' || type === 'float') {
      // Allow empty string or valid number format (including negative and decimal)
      if (newValue !== '' && !/^-?\d*\.?\d*$/.test(newValue)) {
        // If invalid format, don't update
        return
      }
    }

    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        return {
          ...comparison,
          right: { value: newValue, type },
        }
      }
      return comparison
    })

    const newExpression = {
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    }
    setExpression(newExpression)
  }

  const handleVariableSelection = (comparisonIndex: number, variableName: string) => {
    const variableData = workflowManifest.workflowDataInputs.find(
      (data) => data.name === variableName
    )

    const type = getLiteralType(variableName, variableData?.type)

    const newExpressions: WorkflowsComparison[] = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        // Reset the value when variable changes to ensure it's valid for the new type
        return {
          ...comparison,
          left: { name: variableName, source: 'input' },
          right: { value: EMPTY_RIGHT_HAND_SELECTOR_VALUE, type: type },
        }
      }
      return comparison
    })

    const newExpression = {
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    }
    setExpression(newExpression)
  }

  const handleOperatorSelection = (
    comparisonIndex: number,
    operator: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum
  ) => {
    const newExpressions = comparisons.map((comparison, index) => {
      if (index === comparisonIndex) {
        const previousOperator = comparison.operator
        const isArrayOperator = (op: string) => op === 'one_of' || op === 'not_one_of'
        const switchingBetweenSingleAndMultiSelect =
          isArrayOperator(previousOperator) !== isArrayOperator(operator)

        // Reset value when switching between single and multi-select modes
        if (switchingBetweenSingleAndMultiSelect) {
          const variableData = workflowManifest.workflowDataInputs.find(
            (data) => data.name === comparison.left.name
          )
          const newType = getLiteralType(comparison.left.name, variableData?.type, operator)
          const newValue = isArrayOperator(operator)
            ? EMPTY_ARRAY_SELECTOR_VALUE
            : EMPTY_RIGHT_HAND_SELECTOR_VALUE

          return {
            ...comparison,
            operator: operator,
            right: { value: newValue, type: newType },
          }
        }

        return {
          ...comparison,
          operator: operator,
        }
      }
      return comparison
    })

    const newExpression = {
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    }
    setExpression(newExpression)
  }

  const handleAddRequirement = () => {
    const defaultVariable = inputVariables[0]?.value

    if (!defaultVariable) {
      return
    }

    const defaultOperator = getOperatorsForVariable(
      variablesConfig[defaultVariable]?.valueType
    )?.[0]?.value

    if (!defaultOperator) {
      return
    }

    const variableData = workflowManifest.workflowDataInputs.find(
      (data) => data.name === defaultVariable
    )

    const type = getLiteralType(defaultVariable, variableData?.type)

    // Create a new comparison with default values
    const newComparison = {
      left: { name: defaultVariable, source: 'input' },
      operator: defaultOperator,
      right: { value: EMPTY_RIGHT_HAND_SELECTOR_VALUE, type: type },
    }

    // Add the new comparison to the expressions array
    const newExpressions = [...comparisons, newComparison]

    // Update the expression with the new expressions array
    const newExpression = {
      ...expression,
      root: {
        ...expression.root,
        expressions: newExpressions,
      },
    }
    setExpression(newExpression)
  }

  return (
    <Box
      border={{ top: { width: 's1', color: 'grey300', style: 'solid' } }}
      padding="s16"
      gap="s16"
    >
      <Box flexDirection="row" gap="s8" alignItems="baseline" width="full">
        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            label={i18n.t('workflows.sidepanel.condition.paths.type')}
            options={inputVariables.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            value={firstComparison?.left.name}
            onChange={(variableName: string) => handleVariableSelection(0, variableName)}
            error={getFieldErrors(expression, 0).variable}
          />
        </Box>
        <Box style={{ flex: '1', minWidth: '0' }}>
          <F0Select
            size="md"
            hideLabel
            clearable={false}
            label="Operator"
            options={getOperatorsForVariable(firstComparison?.left.name)}
            value={firstComparison?.operator}
            onChange={(operator: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum) =>
              handleOperatorSelection(0, operator)
            }
            error={getFieldErrors(expression, 0).operator}
          />
        </Box>
        <Box style={{ flex: '1', minWidth: '0' }}>
          <RightHandSelector
            variableName={firstComparison?.left.name}
            value={firstComparison?.right.value}
            operator={firstComparison?.operator}
            onChange={(newValue) => {
              const firstComparisonLeftName = firstComparison?.left.name
              if (!firstComparisonLeftName) {
                return
              }
              const variableData = workflowManifest.workflowDataInputs.find(
                (data) => data.name === firstComparisonLeftName
              )
              const type = getLiteralType(
                firstComparisonLeftName,
                variableData?.type,
                firstComparison?.operator
              )
              handleLiteralSelection(0, newValue, type)
            }}
            variablesConfig={variablesConfig}
            errorMessage={
              getFieldState(`comparisons.0.expression.root.expressions.0.right.value`, formState)
                .error?.message
            }
          />
        </Box>
      </Box>

      {/* Requirements list */}
      <Box
        width="fit-content"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs100' }}
        paddingX="s8"
        paddingY="s2"
        inline
      >
        <Text size="100" color="textSecondary" weight="medium">
          {i18n.t('workflows.sidepanel.condition.paths.and')}
        </Text>
      </Box>
      <Box flexDirection="row" gap="s12" alignItems="stretch" paddingLeft={'s20'}>
        {/* vertical line */}
        <Divider vertical />

        <Box flexGrow gap="s4">
          {comparisons.map((comparison, index) => {
            // Skip the first comparison (it's handled separately above)
            if (index === 0) return null

            return (
              <Box
                key={`req-${index}`}
                flexDirection="row"
                gap="s8"
                alignItems="baseline"
                paddingBottom="s8"
                width="full"
              >
                {/* Group of selects */}
                <Box flexDirection="row" alignItems="baseline" gap="s8" flexGrow>
                  <Box style={{ flex: '1', minWidth: '0' }}>
                    <F0Select
                      size="md"
                      hideLabel
                      clearable={false}
                      label="field"
                      options={inputVariables.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                      value={comparison.left.name}
                      onChange={(variableName: string) =>
                        handleVariableSelection(index, variableName)
                      }
                      error={getFieldErrors(expression, index).variable}
                    />
                  </Box>
                  <Box style={{ flex: '1', minWidth: '0' }}>
                    <F0Select
                      size="md"
                      hideLabel
                      clearable={false}
                      label="equals"
                      options={getOperatorsForVariable(comparison.left.name)}
                      value={
                        operatorOptions.find((option) => option.value === comparison.operator)
                          ?.value
                      }
                      onChange={(
                        operator: WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum
                      ) => handleOperatorSelection(index, operator)}
                      error={getFieldErrors(expression, index).operator}
                    />
                  </Box>
                  <Box style={{ flex: '1', minWidth: '0' }}>
                    <RightHandSelector
                      variableName={comparison.left.name}
                      value={comparison.right.value}
                      operator={comparison.operator}
                      onChange={(newValue) => {
                        const comparisonLeftName = comparison.left.name
                        if (!comparisonLeftName) {
                          return
                        }
                        const variableData = workflowManifest.workflowDataInputs.find(
                          (data) => data.name === comparisonLeftName
                        )
                        const type = getLiteralType(
                          comparisonLeftName,
                          variableData?.type,
                          comparison.operator
                        )

                        handleLiteralSelection(index, newValue, type)
                      }}
                      variablesConfig={variablesConfig}
                      errorMessage={
                        getFieldState(
                          `comparisons.0.expression.root.expressions.${index}.right.value`,
                          formState
                        ).error?.message
                      }
                    />
                  </Box>
                </Box>
                <Box alignSelf="flexStart">
                  <F0Button
                    icon={Delete}
                    label={i18n.t('common.delete')}
                    hideLabel
                    onClick={() => handleDeleteComparison(index)}
                    size="md"
                    variant="critical"
                  />
                </Box>
              </Box>
            )
          })}
          <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingBottom="s8">
            <F0Button
              label={i18n.t('workflows.sidepanel.condition.paths.add_requirement')}
              icon={Plus}
              variant="ghost"
              size="sm"
              onClick={handleAddRequirement}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const ConditionPathCard = ({
  expression,
  setExpression,
  variablesConfig,
  priority,
  onRemove,
  canRemove,
}: {
  expression: WorkflowsExpression
  setExpression: (newExpression: WorkflowsExpression) => void
  variablesConfig: VariablesConfig
  priority: number
  onRemove: () => void
  canRemove: boolean
}) => {
  const [open, toggle] = useToggle(true)

  return (
    <CollapsableCard
      open={open}
      Card={
        <ConditionPathCardHeader
          open={open}
          toggle={toggle}
          priority={priority}
          onRemove={onRemove}
          canRemove={canRemove}
        />
      }
      Content={
        open && (
          <ConditionalPathCardContent
            expression={expression}
            setExpression={setExpression}
            variablesConfig={variablesConfig}
          />
        )
      }
      contentPadding="s0"
      contentBackground="grey000White"
    />
  )
}

const ConditionPathHeader = ({ onAddCondition }: { onAddCondition: () => void }) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Text size="300" weight="medium" color="textPrimary">
        {i18n.t('workflows.sidepanel.condition.paths.title')}
      </Text>
      <F0Button
        label={i18n.t('workflows.sidepanel.condition.paths.new')}
        icon={Plus}
        variant="outline"
        size="md"
        onClick={onAddCondition}
      />
    </Box>
  )
}

const ConditionPathContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box gap="s16">
      {children}
      <Divider />
    </Box>
  )
}

type Props = {
  node: NodeUICondition
  variablesConfig: VariablesConfig
}

const ConditionalNodeSidepanel = ({ node, variablesConfig }: Props) => {
  const { setNodesUI, setEdgesUI, edgesUI, nodesUI, workflowManifest } = useWorkflowContext()
  const { openDialog } = useConfirmationDialog()
  const { handleSubmit, setValue, getValues, watch } = useFormContext<ConditionalFormSchema>()

  const comparisons = watch('comparisons')

  const inputVariables = workflowManifest.workflowDataInputs.map((option) => ({
    label: option.name,
    value: option.name,
  }))

  // Ensure at least one comparison exists upon creation
  const hasInitializedComparisonsRef = useRef(false)

  const generateDefaultComparison = (): ConditionalFormSchema['comparisons'][number] => {
    const currentComparisons = getValues('comparisons')
    const newLabel = generateUniqueLabel(currentComparisons)
    const defaultVariable = inputVariables[0]?.value || ''
    const variableData = workflowManifest.workflowDataInputs.find(
      (data) => data.name === defaultVariable
    )

    const type = getLiteralType(defaultVariable, variableData?.type)

    return {
      label: newLabel,
      priority: currentComparisons.length + 1,
      expression: {
        root: {
          operator: 'and',
          expressions: [
            {
              left: { name: defaultVariable, source: 'input' },
              operator: 'eq',
              right: { value: EMPTY_RIGHT_HAND_SELECTOR_VALUE, type: type },
            },
          ],
        },
      },
    }
  }

  useUnsafeEffect(() => {
    if (hasInitializedComparisonsRef.current) return
    if (comparisons.length !== 0) return

    const initializeComparisons = () => {
      const currentComparisons = getValues('comparisons')

      const newComparisons = [...currentComparisons, generateDefaultComparison()]
      setValue('comparisons', newComparisons, { shouldDirty: true })
    }

    initializeComparisons()
    hasInitializedComparisonsRef.current = true
  }, [comparisons.length, getValues, setValue, inputVariables, workflowManifest.workflowDataInputs])

  if (node.type !== 'condition') return null

  // Helper function for updating a specific comparison's expression
  const setExpression = (newExpression: WorkflowsExpression, label: string) => {
    const currentComparisons = getValues('comparisons')
    const updatedComparisons = currentComparisons.map((comp) =>
      comp.label === label ? { ...comp, expression: newExpression } : comp
    )
    setValue('comparisons', updatedComparisons, { shouldDirty: true })
  }

  // Helper function to add a new comparison
  const addComparison = () => {
    const currentComparisons = getValues('comparisons')

    const newComparisons = [...currentComparisons, generateDefaultComparison()]
    setValue('comparisons', newComparisons, { shouldDirty: true })
  }

  // Helper function to remove a comparison
  const removeComparison = (label: string) => {
    const currentComparisons = getValues('comparisons')
    const updatedComparisons = currentComparisons
      .filter((comp) => comp.label !== label)
      .map((comp, index) => ({ ...comp, priority: index + 1 })) // Reassign priorities
    setValue('comparisons', updatedComparisons, { shouldDirty: true })
  }

  // Closes form by deselecting all nodes
  const handleCancel = () => {
    setNodesUI((prev) => prev.map((n) => ({ ...n, selected: false })))
  }

  // Updates global state and deselects all nodes
  const handleSave = () => {
    const { title, description, comparisons: formComparisons } = getValues()

    // If the title or description is empty, set the default title or description
    // Otherwise, assume it was already set and keep it as is
    const shouldSetDefaultTitle = (title ?? '').trim() === ''
    const shouldSetDefaultDescription = (description ?? '').trim() === ''

    const updatedNode: NodeUICondition = {
      ...node,
      data: {
        ...node.data,
        title: shouldSetDefaultTitle
          ? i18n.t('workflows.building_blocks.condition.title')
          : title || '',
        description: shouldSetDefaultDescription
          ? i18n.t('workflows.building_blocks.condition.description')
          : description || '',
        comparisons: formComparisons,
      },
    }

    setNodesUI((nodes) => {
      // Generate all new child nodes (conditionIf + else) in correct order
      const newChildNodes = createAllConditionChildNodes(
        updatedNode.id,
        updatedNode.data.comparisons
      )

      // All nodes except for children about to be removed
      const nodesWithoutOldChildren = nodes.filter((n) => {
        const isChildNode = edgesUI.some((edge) => edge.source === node.id && edge.target === n.id)
        return !isChildNode
      })

      // Update the main condition node and deselect all nodes
      return [
        ...nodesWithoutOldChildren.map((n) =>
          n.id === updatedNode.id ? { ...updatedNode, selected: false } : { ...n, selected: false }
        ),
        ...newChildNodes,
      ]
    })

    // Update edges: remove old condition split edges and add new ones
    setEdgesUI((edges) => {
      // Remove old edges from this condition to its children
      const edgesWithoutOldSplits = edges.filter((edge) => {
        return edge.source !== node.id
      })

      // Generate all new edges for the updated comparisons (conditionIf + else)
      const newChildEdges = createAllConditionChildEdges(
        updatedNode.id,
        updatedNode.data.comparisons
      )

      return [...edgesWithoutOldSplits, ...newChildEdges]
    })
  }

  const onSubmit = () => {
    handleSubmit(handleSave, (_fields) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 3000,
      })
    })()
  }

  // Handle delete with confirmation dialog
  const handleDeleteWithConfirmation = () => {
    openDialog({
      title: i18n.t('workflows.sidepanel.condition.delete_confirmation.title'),
      description: i18n.t('workflows.sidepanel.condition.delete_confirmation.description'),
      confirmLabel: i18n.t('common.delete'),
      onConfirm: handleDelete,
    })
  }

  // Deletes the node and its direct children (special logic for conditional nodes)
  const handleDelete = () => {
    // Find direct children of this node using edges
    const directChildren = nodesUI.filter((childNode) =>
      edgesUI.some((edge) => edge.source === node.id && edge.target === childNode.id)
    )

    const childrenIds = directChildren.map((child) => child.id)
    const nodeToDeleteId = node.id

    // Remove the main node and its direct children from nodes
    setNodesUI((nodes) =>
      nodes.filter((n) => n.id !== nodeToDeleteId && !childrenIds.includes(n.id))
    )

    // Remove all edges connected to the deleted nodes (both incoming and outgoing)
    setEdgesUI((edges) =>
      edges.filter((edge) => {
        const nodesToDelete = [nodeToDeleteId, ...childrenIds]
        return !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target)
      })
    )
  }

  return (
    <SidepanelWrapper
      width="508px"
      footer={<SidepanelFooter onCancel={handleCancel} onSave={onSubmit} isValid />}
    >
      <SidepanelHeader
        nodeId={node.id}
        handleDelete={handleDeleteWithConfirmation}
        backgroundColor="#CDECEF80"
        icon={
          <OrganizationIcon width={22} height={22} style={{ color: '#07A2AD', strokeWidth: 1.5 }} />
        }
        title={i18n.t('workflows.sidepanel.condition.edit_title')}
        onClose={handleCancel}
      />
      <BasicInputs />
      <ConditionPathContainer>
        <ConditionPathHeader onAddCondition={() => addComparison()} />
        {comparisons.map((comparison) => (
          <ConditionPathCard
            key={comparison.label}
            expression={comparison.expression}
            setExpression={(newExpression) => setExpression(newExpression, comparison.label)}
            variablesConfig={variablesConfig}
            priority={comparison.priority}
            onRemove={() => removeComparison(comparison.label)}
            canRemove={comparisons.length > 1}
          />
        ))}
      </ConditionPathContainer>
    </SidepanelWrapper>
  )
}

export default ConditionalNodeSidepanel
